import type { ColorAttribute, merchantProductOptionType } from './helpers';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;
const normalizeID = (id: unknown): string | null => (typeof id === 'string' ? id : typeof id === 'number' && Number.isFinite(id) ? String(id) : null);

const normalizeAttribute = (attribute: unknown): ColorAttribute | null => {
    if (!isRecord(attribute) || !isRecord(attribute.Value) || typeof attribute.Label !== 'string' || typeof attribute.Value.Label !== 'string') return null;
    const id = normalizeID(attribute.Id);
    const valueID = normalizeID(attribute.Value.Id);
    // Keep legacy numeric IDs and extra fields without mutating the saved mapping.
    return id === null || valueID === null
        ? null
        : { ...attribute, Id: id, Label: attribute.Label, Value: { ...attribute.Value, Id: valueID, Label: attribute.Value.Label } };
};

export const normalizeAttributes = (attributes: unknown): ColorAttribute[] | null => {
    if (!Array.isArray(attributes)) return null;
    const normalized = attributes.map(normalizeAttribute);
    return normalized.every((attribute) => attribute !== null) ? normalized : null;
};

export function readAttributes(code: string): ColorAttribute[] | null {
    try {
        return normalizeAttributes(JSON.parse(code || '[]'));
    } catch {
        return null;
    }
}

/** Derive only imported options, retaining unresolved values and grouping by identity rather than array position. */
export const buildVariantOptions = (colors: readonly { code: string }[]): merchantProductOptionType[] => {
    const attributes = colors.flatMap((color) => readAttributes(color.code) ?? []);
    const options = Array.from(new Map(attributes.map((attribute) => [JSON.stringify([attribute.Id, attribute.Label]), attribute])).values());
    return options.map((option) => ({
        optionID: option.Id,
        label: option.Label,
        isGlobal: false,
        values: Array.from(
            new Map(
                attributes
                    .filter((attribute) => attribute.Id === option.Id && attribute.Label === option.Label)
                    .map(({ Value }) => [JSON.stringify([Value.Id, Value.Label]), { valueID: Value.Id, label: Value.Label }])
            ).values()
        )
    }));
};
